import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../../../common/state/store";
import type { IAlert } from "../../../model/model";
import type { IAlertsState } from "../../../model/store";

export interface ISetAlertPayload {
  isOpen?: boolean;
  msg: string;
  type?: string;
  subMsg?: string | null;
  showCloseButton?: boolean;
  customTimeoutInMs?: number;
  id?: string;
}

export type ISetHideAlertPayload = Pick<IAlert, "id"> | null | undefined;

const initialState: IAlertsState = {
  alertsQueue: [],
  publishWarningText: "",
};

function resetKey<K extends keyof IAlertsState>(
  state: IAlertsState,
  initial: IAlertsState,
  key: K,
) {
  state[key] = initial[key];
}

export const alertsSlice = createSlice({
  name: "alerts",
  initialState,
  reducers: {
    setAlert: (state: IAlertsState, action: PayloadAction<ISetAlertPayload>) => {
      const alert: IAlert = {
        alertIsOpen: action.payload.isOpen ?? false,
        alertMsg: action.payload.msg,
        alertStyle: action.payload.type,
        alertSubMsg: action.payload.subMsg ? action.payload.subMsg.toString() : null,
        alertShowCloseButton: action.payload.showCloseButton
          ? action.payload.showCloseButton
          : true,
        customTimeoutInMs: action.payload.customTimeoutInMs ? action.payload.customTimeoutInMs : 0,
        id: action.payload.id ?? String(Date.now()) + ":" + String(Math.random()),
      };
      state.alertsQueue = [alert];
    },
    setHideAlert: (state: IAlertsState, action: PayloadAction<ISetHideAlertPayload>) => {
      if (!action.payload) {
        return;
      }
      const index = state.alertsQueue.findIndex((elt) => elt.id === action.payload?.id);
      if (index >= 0) {
        state.alertsQueue.splice(index);
      }
    },
    clearAlerts: (state: IAlertsState) => {
      (Object.keys(state) as Array<keyof typeof state>).forEach((key) => {
        resetKey(state, initialState, key);
      });
    },
    setPublishWarningText: (state: IAlertsState, action: PayloadAction<string>) => {
      state.publishWarningText = action.payload;
    },
  },
});

export const alertsReducer = alertsSlice.reducer;

export const { setAlert, setHideAlert, clearAlerts, setPublishWarningText } = alertsSlice.actions;

export const getAlert = (state: RootState) => state.alerts;
export const getPublishWarningText = (state: RootState) => state.alerts.publishWarningText;

export default alertsSlice.reducer;
